<template>
  <div class="kpi-line-menu">
    <DropDownMenu
      :dropdownItems="visibleMenuItems"
      :isOpen="isMenuOpen"
      @onClick="handleMenuClick"
      :menuWidth="true"
      :width="211"
      :translate-x-offset="-36"
      :translate-y-offset="30"
    >
      <template #activator>
        <div class="icon-container">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="icon-svg"
            role="img"
            aria-label="KPI actions"
          >
            <path
              d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z"
              stroke="#344054"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
      </template>
    </DropDownMenu>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import DropDownMenu from "@components/DropDownMenu/DropDownMenu.vue";

// Rule 11 (KPIs Catalog): the statuses each row operation is allowed from, in Figma order.
// Clone creates a new draft rather than operating on this row, so it is offered for every status.
const MENU_ITEMS = [
  { name: "Activate", value: "activate", icon: "icons/dropdown/activate.svg", statuses: ["DRAFT"] },
  {
    name: "Delete",
    value: "delete",
    icon: "icons/dropdown/trash.svg",
    statuses: ["DRAFT"],
    destructive: true,
    negative: true,
  },
  { name: "Retire", value: "retire", icon: "icons/dropdown/activate.svg", statuses: ["ACTIVE"] },
  { name: "Reactivate", value: "reactivate", icon: "icons/dropdown/activate.svg", statuses: ["RETIRED"] },
  {
    name: "Rename",
    value: "rename",
    icon: "icons/dropdown/rename.svg",
    statuses: ["DRAFT", "ACTIVE", "RETIRED"],
  },
  { name: "Clone", value: "clone", icon: "icons/dropdown/copy-02.svg", statuses: ["DRAFT", "ACTIVE", "RETIRED", "ARCHIVED"] },
  { name: "Archive", value: "archive", icon: "icons/dropdown/archive-new.svg", statuses: ["RETIRED"] },
  { name: "Unarchive", value: "unarchive", icon: "icons/dropdown/activate.svg", statuses: ["ARCHIVED"] },
  {
    name: "Unarchive and rename",
    value: "unarchive-and-rename",
    icon: "icons/dropdown/rename.svg",
    statuses: ["ARCHIVED"],
  },
];

const props = defineProps({
  status: {
    type: String,
    default: "Draft",
    validator: (s) => ["DRAFT", "ACTIVE", "RETIRED", "ARCHIVED"].includes(String(s).toUpperCase()),
  },
  // Rule 11 does not gate any row operation on a pending draft, so it does not change the list.
  hasPendingChanges: {
    type: Boolean,
    default: false,
  },
  kpiId: {
    type: [String, Number],
    default: null,
  },
  kpiName: {
    type: String,
    default: "",
  },
  menuOpen: {
    type: Boolean,
    default: false,
  },
  // Action values (e.g. "clone") a page that has not wired them yet can leave out of the menu.
  hiddenActions: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  "activate",
  "delete",
  "retire",
  "reactivate",
  "rename",
  "clone",
  "archive",
  "unarchive",
  "unarchive-and-rename",
]);

const isMenuOpen = ref(props.menuOpen);

watch(
  () => props.menuOpen,
  (newVal) => {
    isMenuOpen.value = newVal;
  }
);

const visibleMenuItems = computed(() => {
  const status = String(props.status).toUpperCase();
  return MENU_ITEMS.filter(
    (item) => item.statuses.includes(status) && !props.hiddenActions.includes(item.value)
  ).map(
    ({ statuses, ...item }) => item
  );
});

function handleMenuClick(item) {
  emit(item.value, props.kpiId);
}

defineExpose({ handleMenuClick, visibleMenuItems, isMenuOpen });
</script>

<style scoped>
.kpi-line-menu {
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

.icon-svg {
  display: block;
}
</style>

